import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Modal, Select } from '@Sage/hoi-poi-ui';

import Context from 'managers/Context';
import { COMPANIES } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const propTypes = {
    edit: PropTypes.object,
    idCompany: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isOpen: PropTypes.bool,
    onAfterClose: PropTypes.func,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    onRequestClose: PropTypes.func,
};

const AddressModal = memo(
    ({ edit, idCompany, isOpen, onAfterClose, onCancel, onConfirm, onRequestClose }) => {
        const [formData, setFormData] = useState({
            id: null,
            idCompany: parseInt(idCompany, 10) || -1,
            description: '',
            address: '',
            address2: '',
            postCode: '',
            province: '',
            city: '',
            idCountry: null,
            country: '',
        });
        const [errors, setErrors] = useState({});
        const [loading, setLoading] = useState(false);
        const [countryOptions, setCountryOptions] = useState([]);

        useEffect(() => {
            Context.serverListManager.getList('tblCountries').then((list) => {
                const options = list.map((item) => ({
                    label: item.label,
                    value: String(item.value),
                }));
                setCountryOptions(options);
            });
        }, []);

        useEffect(() => {
            if (!edit) {
                setFormData({
                    id: null,
                    idCompany: parseInt(idCompany, 10) || -1,
                    description: '',
                    address: '',
                    address2: '',
                    postCode: '',
                    province: '',
                    city: '',
                    idCountry: null,
                    country: '',
                });
                setErrors({});
            } else {
                setFormData({ ...edit });
            }
            setLoading(false);
        }, [edit, idCompany]);

        const handleOnChange = useCallback(
            (fieldId) => (value) => {
                if (fieldId === 'idCountry') {
                    const selectedOption = countryOptions.find((opt) => opt.value === value?.value);
                    setFormData((prev) => ({
                        ...prev,
                        idCountry: value?.value ? parseInt(value.value, 10) : null,
                        country: selectedOption?.label || '',
                    }));
                } else {
                    setFormData((prev) => ({ ...prev, [fieldId]: value }));
                }
                if (errors[fieldId]) {
                    setErrors((prev) => ({ ...prev, [fieldId]: undefined }));
                }
            },
            [countryOptions, errors],
        );

        const validateForm = useCallback(() => {
            const newErrors = {};
            if (!formData.description) {
                newErrors.description = getLiteral('helptext_required_field');
            }
            if (!formData.address) {
                newErrors.address = getLiteral('helptext_required_field');
            }
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }, [formData]);

        const onSave = useCallback(() => {
            if (!validateForm()) return;

            setLoading(true);
            if (!formData.id) {
                Context.entityManager.getEntitiesManager(COMPANIES).addCompanyAddress(
                    formData,
                    () => {
                        onConfirm?.({ ...formData, isNew: true });
                    },
                    (error) => {
                        setLoading(false);
                        console.error('error saving address:', error);
                    },
                );
            } else {
                Context.entityManager.getEntitiesManager(COMPANIES).updateCompanyAddress(
                    formData,
                    formData.id,
                    () => {
                        onConfirm?.(formData);
                    },
                    (error) => {
                        setLoading(false);
                        console.error('error updating address:', error);
                    },
                );
            }
        }, [formData, onConfirm, validateForm]);

        const onDelete = useCallback(() => {
            setLoading(true);
            Context.entityManager.getEntitiesManager(COMPANIES).deleteCompanyAddress(
                formData.id,
                () => {
                    onConfirm?.({ id: formData.id, isDeleted: true });
                },
                (error) => {
                    setLoading(false);
                    console.error('error deleting address:', error);
                },
            );
        }, [formData, onConfirm]);

        const labels = useMemo(
            () => ({
                address: getLiteral('label_address'),
                address2: getLiteral('label_address_detail'),
                city: getLiteral('label_city'),
                description: getLiteral('label_address_description'),
                idCountry: getLiteral('label_country'),
                modalCancelText: getLiteral('action_cancel'),
                modalConfirmText: getLiteral('action_save'),
                modalDeleteText: getLiteral('action_delete_address'),
                modalTitle: formData?.id
                    ? getLiteral('title_action_edit_shipment_address')
                    : getLiteral('action_add_address'),
                placeholderSelect: getLiteral('label_selectone'),
                placeholderText: getLiteral('placeholder_text_field'),
                postCode: getLiteral('label_postal_code'),
                province: getLiteral('label_provinceregion'),
            }),
            [formData?.id],
        );

        const countryValue = useMemo(() => {
            if (!formData.idCountry) return null;
            return countryOptions.find((opt) => opt.value === String(formData.idCountry)) || null;
        }, [countryOptions, formData.idCountry]);

        const modalClasses = useMemo(
            () => ({
                content: 'fm-shipping-address-modal__content',
                overlay: 'fm-shipping-address-modal-overlay',
            }),
            [],
        );

        return (
            <Modal
                className="fm-shipping-address-modal"
                classes={modalClasses}
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                size="medium"
                title={labels.modalTitle}
                useCornerClose
                onAfterClose={onAfterClose}
            >
                <div className="fm-shipping-address-modal__form">
                    <Input
                        className="fm-shipping-address-modal__field"
                        error={errors.description}
                        isFullWidth
                        isRequired
                        label={labels.description}
                        onChange={handleOnChange('description')}
                        placeholder={labels.placeholderText}
                        value={formData.description}
                    />
                    <Input
                        className="fm-shipping-address-modal__field"
                        error={errors.address}
                        isFullWidth
                        isRequired
                        label={labels.address}
                        onChange={handleOnChange('address')}
                        placeholder={labels.placeholderText}
                        value={formData.address}
                    />
                    <Input
                        className="fm-shipping-address-modal__field"
                        isFullWidth
                        label={labels.address2}
                        onChange={handleOnChange('address2')}
                        placeholder={labels.placeholderText}
                        value={formData.address2}
                    />
                    <div className="fm-shipping-address-modal__row">
                        <Input
                            className="fm-shipping-address-modal__field"
                            isFullWidth
                            label={labels.postCode}
                            onChange={handleOnChange('postCode')}
                            placeholder={labels.placeholderText}
                            value={formData.postCode}
                        />
                        <Input
                            className="fm-shipping-address-modal__field"
                            isFullWidth
                            label={labels.city}
                            onChange={handleOnChange('city')}
                            placeholder={labels.placeholderText}
                            value={formData.city}
                        />
                    </div>
                    <div className="fm-shipping-address-modal__row">
                        <Input
                            className="fm-shipping-address-modal__field"
                            isFullWidth
                            label={labels.province}
                            onChange={handleOnChange('province')}
                            placeholder={labels.placeholderText}
                            value={formData.province}
                        />
                        <Select
                            className="fm-shipping-address-modal__field"
                            isFullWidth
                            label={labels.idCountry}
                            onChange={handleOnChange('idCountry')}
                            options={countryOptions}
                            placeholder={labels.placeholderSelect}
                            value={countryValue}
                            useMenuPortal={false}
                            menuPosition="absolute"
                        />
                    </div>
                </div>
                <div className="fm-shipping-address-modal__footer">
                    {edit && (
                        <Button
                            type="secondary-error"
                            onClick={onDelete}
                            isDisabled={loading}
                            icon="trash"
                        >
                            {labels.modalDeleteText}
                        </Button>
                    )}
                    <div className="fm-shipping-address-modal__footer-actions">
                        <Button type="terciary" onClick={onCancel} isDisabled={loading}>
                            {labels.modalCancelText}
                        </Button>
                        <Button onClick={onSave} isDisabled={loading}>
                            {labels.modalConfirmText}
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    },
);

AddressModal.displayName = 'AddressModal';
AddressModal.propTypes = propTypes;

export default AddressModal;
